import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import { useTableData } from "../../hooks/useTableData";
import { bbpsReportConfig } from "config/tableConfigs";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";

// ─────────────────────────────────────────────
// STATUS BADGE
// ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending: { bg: "#fff8e1", color: "#f59f00", border: "#ffe082", label: "Pending" },
    success: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7", label: "Success" },
    failed:  { bg: "#fce4ec", color: "#c62828", border: "#ef9a9a", label: "Failed"  },
  };
  const s = map[status?.toLowerCase()] || map.pending;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
    }}>
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────
// INFO ROW (used inside modal)
// ─────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div style={{
    display: "flex", justifyContent: "space-between",
    alignItems: "center", padding: "6px 0",
    borderBottom: "1px solid #f0f0f0", fontSize: 13,
  }}>
    <span style={{ color: "#888", minWidth: 120 }}>{label}</span>
    <span style={{ fontWeight: 600, color: "#1a1a1a", textAlign: "right" }}>{value || "—"}</span>
  </div>
);

// ─────────────────────────────────────────────
// ADMIN UPDATE MODAL
// ─────────────────────────────────────────────
const AdminUpdateModal = ({ isOpen, onClose, report, onSuccess }) => {
  const [status, setStatus]   = useState("success");
  const [utr, setUtr]         = useState("");
  const [txnId, setTxnId]     = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [done, setDone]       = useState(false);

  if (!isOpen || !report) return null;

  const reset = () => {
    setUtr(""); setTxnId(""); setError("");
    setStatus("success"); setDone(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!utr.trim()) { setError("UTR number is required."); return; }
    setError(""); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("report_id", report.id);
      fd.append("status",    status);
      fd.append("utr",       utr.trim());
      fd.append("txn_id",    txnId.trim());

      const res = await fetchWrapper(
        "https://api.mokshiq.in/update_bbps1_bill_mannual",
        { method: "POST", body: fd }
      );

      if (res?.status) {
        setDone(true);
        setTimeout(() => { onSuccess(report.id, status); handleClose(); }, 1400);
      } else {
        setError(res?.message || "Update failed. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = status === "success";

  return (
    // Backdrop
    <div onClick={handleClose} style={{
      position: "fixed", inset: 0,
      background: "rgba(10,10,20,0.55)",
      backdropFilter: "blur(3px)",
      zIndex: 5000,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      {/* Modal card — stop propagation so clicking inside doesn't close */}
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16,
        width: "100%", maxWidth: 500,
        boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
        overflow: "hidden",
        fontFamily: "'Segoe UI', sans-serif",
      }}>

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
          padding: "20px 24px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              Update Offline BBPS
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, marginTop: 2 }}>
              Transaction #{report.id}
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: "rgba(255,255,255,0.15)", border: "none",
            borderRadius: 8, width: 32, height: 32,
            color: "#fff", fontSize: 18, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>×</button>
        </div>

        <div style={{ padding: 24 }}>

          {/* Success state */}
          {done ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%",
                background: isSuccess ? "#e8f5e9" : "#fce4ec",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px", fontSize: 28,
              }}>
                {isSuccess ? "✓" : "✗"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 16, color: isSuccess ? "#2e7d32" : "#c62828" }}>
                Marked as {isSuccess ? "Success" : "Failed"}
              </div>
              <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
                Wallets have been {isSuccess ? "confirmed" : "reversed"}.
              </div>
            </div>
          ) : (
            <>
              {/* Transaction summary */}
              <div style={{
                background: "#f8f9fc", borderRadius: 10,
                padding: "12px 16px", marginBottom: 20,
                border: "1px solid #ebebeb",
              }}>
                <InfoRow label="BC Name"      value={report["BC Name"]} />
                <InfoRow label="Consumer No." value={report.number} />
                <InfoRow label="Operator"     value={report.operator} />
                <InfoRow label="Amount"       value={report.amount ? `₹${report.amount}` : null} />
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "center", paddingTop: 8, fontSize: 13,
                }}>
                  <span style={{ color: "#888" }}>Current Status</span>
                  <StatusBadge status={report.status} />
                </div>
              </div>

              {/* Status selector */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#444", display: "block", marginBottom: 8 }}>
                  New Status <span style={{ color: "#e53935" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 10 }}>
                  {["success", "failed"].map(s => (
                    <div key={s} onClick={() => setStatus(s)} style={{
                      flex: 1, textAlign: "center", padding: "10px 0",
                      borderRadius: 10, cursor: "pointer",
                      border: `2px solid ${status === s
                        ? (s === "success" ? "#43a047" : "#e53935")
                        : "#e0e0e0"}`,
                      background: status === s
                        ? (s === "success" ? "#e8f5e9" : "#fce4ec")
                        : "#fafafa",
                      color: status === s
                        ? (s === "success" ? "#2e7d32" : "#c62828")
                        : "#9e9e9e",
                      fontWeight: 700, fontSize: 14,
                      transition: "all 0.15s",
                    }}>
                      {s === "success" ? "✓ Success" : "✗ Failed"}
                    </div>
                  ))}
                </div>
              </div>

              {/* UTR */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#444", display: "block", marginBottom: 6 }}>
                  UTR Number <span style={{ color: "#e53935" }}>*</span>
                </label>
                <input
                  value={utr}
                  onChange={e => setUtr(e.target.value)}
                  placeholder="Enter UTR / reference number"
                  style={{
                    width: "100%", padding: "10px 14px",
                    border: `1.5px solid ${error && !utr ? "#e53935" : "#dde1e7"}`,
                    borderRadius: 9, fontSize: 14, outline: "none",
                    transition: "border-color 0.15s", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = "#3949ab"}
                  onBlur={e => e.target.style.borderColor = "#dde1e7"}
                />
              </div>

              {/* TXN ID */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontWeight: 600, fontSize: 13, color: "#444", display: "block", marginBottom: 6 }}>
                  External TXN ID
                  <span style={{ color: "#aaa", fontWeight: 400, marginLeft: 6 }}>(optional)</span>
                </label>
                <input
                  value={txnId}
                  onChange={e => setTxnId(e.target.value)}
                  placeholder="Enter external transaction ID"
                  style={{
                    width: "100%", padding: "10px 14px",
                    border: "1.5px solid #dde1e7",
                    borderRadius: 9, fontSize: 14, outline: "none",
                    transition: "border-color 0.15s", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = "#3949ab"}
                  onBlur={e => e.target.style.borderColor = "#dde1e7"}
                />
              </div>

              {/* Warning for failed */}
              {status === "failed" && (
                <div style={{
                  background: "#fff8e1", border: "1px solid #ffe082",
                  borderRadius: 9, padding: "10px 14px",
                  fontSize: 13, color: "#e65100", marginBottom: 16,
                  display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span>
                    Marking as <strong>Failed</strong> will <strong>reverse all wallet deductions</strong> for
                    this transaction. This action cannot be undone.
                  </span>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{
                  background: "#fce4ec", border: "1px solid #ef9a9a",
                  borderRadius: 9, padding: "10px 14px",
                  fontSize: 13, color: "#b71c1c", marginBottom: 16,
                }}>
                  {error}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleClose} disabled={loading} style={{
                  flex: 1, padding: "11px 0", borderRadius: 9,
                  border: "1.5px solid #dde1e7", background: "#fff",
                  color: "#555", fontWeight: 600, fontSize: 14, cursor: "pointer",
                }}>
                  Cancel
                </button>
                <button onClick={handleSubmit} disabled={loading} style={{
                  flex: 2, padding: "11px 0", borderRadius: 9, border: "none",
                  background: isSuccess
                    ? "linear-gradient(135deg, #388e3c, #43a047)"
                    : "linear-gradient(135deg, #c62828, #e53935)",
                  color: "#fff", fontWeight: 700, fontSize: 14,
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.8 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}>
                  {loading && (
                    <span style={{
                      width: 16, height: 16, borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      animation: "spin 0.7s linear infinite",
                      display: "inline-block",
                    }} />
                  )}
                  {loading ? "Updating..." : `Mark as ${isSuccess ? "Success" : "Failed"}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────
const OfflineBBPSReportsPage = () => {
  const { userid, role } = useSession();
  const isAdmin = role === "Admin" || role === "Main Admin";

  const [loading, setLoading]               = useState(false);
  const [showModal, setShowModal]           = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const getTodayRange = () => {
    const t = new Date().toISOString().split("T")[0];
    return { start: t, end: t };
  };
  const { start, end } = getTodayRange();

  const [apiUrl, setApiUrl] = useState(
    `https://api.mokshiq.in/get_offline_bbps_report?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  const tableData = useTableData(apiUrl, 0, bbpsReportConfig);

  // ── Refetch after admin update ──
  const handleUpdateSuccess = (reportId, newStatus) => {
    setApiUrl(prev => {
      const base = prev.split("&_r=")[0];
      return `${base}&_r=${Date.now()}`;
    });
  };

  const handleDateFilter = (s, e) => {
    setApiUrl(
      `https://api.mokshiq.in/get_offline_bbps_report?user=${userid}&filter_date=${s}&end_date=${e}`
    );
  };

  // ── Admin-only edit handler ──
  const handleEdit = (report) => {
    if (!isAdmin) return;
    if (report.status !== "pending") {
      alert(`This transaction is already "${report.status}" and cannot be updated.`);
      return;
    }
    setSelectedReport(report);
    setShowModal(true);
  };

  // ── Role-based column filtering ──
  const filteredConfig = useMemo(() => {
    const base = {
      ...bbpsReportConfig,
      columns: [...bbpsReportConfig.columns],
      actions: {
        ...bbpsReportConfig.actions,
        edit: isAdmin, // ← true for Admin/Main Admin, false for others
      },
    };

    const roleMap = {
      Retailer: [
        "id", "txn_id", "operator", "product", "number", "amount",
        "status", "Opening Balance BC", "BC Commission", "BC TDS",
        "Closing Balance BC", "created_at",
      ],
      "API Partner": [
        "id", "txn_id", "operator", "product", "number", "amount",
        "status", "Opening Balance BC", "BC Commission", "BC TDS",
        "Closing Balance BC", "created_at",
      ],
      Distributer: [
        "id", "txn_id", "operator", "product", "number", "amount",
        "status", "Commission Dist", "TDS Dist", "created_at",
      ],
      "Super Distributer": [
        "id", "txn_id", "operator", "product", "number", "amount",
        "status", "Commission SuperDist", "TDS SuperDist", "created_at",
      ],
      "White label": [
        "id", "BC Name", "Phone", "txn_id", "operator", "product",
        "number", "amount", "status",
        "Opening Balance", "Commission", "TDS", "Charge", "GST", "Closing Balance",
        "Opening Balance BC", "BC Commission", "BC TDS", "Closing Balance BC",
        "created_at",
      ],
      Admin: [
        "id", "BC Name", "Phone", "txn_id", "operator", "product",
        "number", "amount", "status",
        "Opening Balance", "Commission", "TDS", "Charge", "GST", "Closing Balance",
        "Opening Balance BC", "BC Commission", "BC TDS", "Closing balance BC",
        "Transection Flow", "Wallet Txn", "TXN ID", "api_response", "created_at",
      ],
      "Main Admin": [
        "id", "BC Name", "Phone", "txn_id", "operator", "product",
        "number", "amount", "status",
        "Opening Balance", "Commission", "TDS", "Charge", "GST", "Closing Balance",
        "Opening Balance BC", "BC Commission", "BC TDS", "Closing balance BC",
        "Opening Balance MAdmin", "Commission MAdmin", "TDS MAdmin",
        "Charge MAdmin", "Closing balance MAdmin",
        "Transection Flow", "Wallet Txn", "TXN ID", "api_response", "created_at",
      ],
    };

    const allowed = roleMap[role] || [];
  base.columns = base.columns.filter(c => allowed.includes(c.key));
  
    return base;
  }, [role, isAdmin]);

  return (
    <div>
      {/* Full-page loader */}
      {loading && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(255,255,255,0.75)",
          zIndex: 3000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <PageWrapper config={{ ...bbpsReportConfig, title: "Offline BBPS Reports" }} onDateFilter={handleDateFilter}>

        {/* Admin hint banner */}
        {isAdmin && (
          <div style={{
            background: "linear-gradient(90deg, #e8eaf6, #f3f4fd)",
            border: "1px solid #c5cae9",
            borderRadius: 10, padding: "10px 18px",
            marginBottom: 14, fontSize: 13, color: "#3949ab",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            Click the <strong>Edit</strong> button on any <strong>Pending</strong> transaction to approve or reject it.
          </div>
        )}

        <DataTable
          config={filteredConfig}
          tableData={tableData}
          onView={() => {}}
          onEdit={isAdmin ? handleEdit : undefined}
          onDelete={() => {}}
          onExport={() => {}}
          onDeleteSelected={() => {}}
        />
      </PageWrapper>

      {/* Admin Update Modal */}
      {isAdmin && (
        <AdminUpdateModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedReport(null); }}
          report={selectedReport}
          onSuccess={handleUpdateSuccess}
        />
      )}
    </div>
  );
};

export default OfflineBBPSReportsPage;